import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, hr } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import EmployeeHistorySheet from "../components/EmployeeHistorySheet";
import PayrollReportCard from "../components/PayrollReportCard";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { BarChart3, CalendarDays, Users as UsersIcon, TrendingUp, LineChart as LineChartIcon, PieChart as PieIcon, Search, Wallet, Send, CheckCircle2, AlertCircle, MailWarning } from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  LineChart, Line,
} from "recharts";

const MONTH_NAMES = ["Sij", "Velj", "Ožu", "Tra", "Svi", "Lip", "Srp", "Kol", "Ruj", "Lis", "Stu", "Pro"];
const COLORS = ["#0B2545", "#C9A961", "#7B9E89", "#BFAA88", "#6C8DAE", "#A86464", "#8B7355", "#4A7C7C"];

function StatCard({ icon: Icon, label, value, sub }) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-secondary/60 flex items-center justify-center">
            <Icon className="w-5 h-5 text-primary"/>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
            <p className="serif text-2xl font-semibold leading-tight">{value}</p>
            {sub && <p className="text-[11px] text-muted-foreground mt-0.5">{sub}</p>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function Statistika() {
  const [data, setData] = useState(null);
  const [yoy, setYoy] = useState(null);
  const [fund, setFund] = useState(null);
  const [fundQ, setFundQ] = useState("");
  const [onlyPending, setOnlyPending] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [sending, setSending] = useState(false);
  const [historyEmployee, setHistoryEmployee] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/stats")
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Greška"));
    api.get("/admin/stats/yoy")
      .then(r => setYoy(r.data))
      .catch(() => { /* non-blocking */ });
    api.get("/admin/stats/fund-breakdown")
      .then(r => setFund(r.data))
      .catch(() => { /* non-blocking */ });
  }, []);

  if (err) return <div className="p-8 text-red-600">{err}</div>;
  if (!data) return <div className="p-8 text-muted-foreground">Učitavanje…</div>;

  const o = data.overview;
  const totalPendingApprovals = (o.vacation.pending || 0) + (o.wfh.pending || 0);
  const totalApproved = (o.vacation.approved || 0) + (o.vacation.decided || 0) + (o.vacation.sent || 0);
  const monthData = data.by_month.map(m => ({ month: MONTH_NAMES[m.month - 1], days: m.days }));
  const katedraData = data.by_katedra.slice(0, 10).map(k => ({
    name: k.name.length > 22 ? k.name.slice(0, 22) + "…" : k.name,
    GO: k.go_days, WFH: k.wfh_count, fullName: k.name,
  }));
  const statusPie = [
    { name: "U postupku suglasnosti", value: o.vacation.pending || 0 },
    { name: "Suglasan od voditelja", value: o.vacation.approved || 0 },
    { name: "Rješenje", value: o.vacation.decided || 0 },
    { name: "Poslano", value: o.vacation.sent || 0 },
    { name: "Odbijeno", value: o.vacation.rejected || 0 },
  ].filter(x => x.value > 0);

  // ── YoY comparative series — re-map month numbers to short names ──────────
  const yoyMap = (rows) => (rows || []).map(r => ({ ...r, month: MONTH_NAMES[r.month - 1] }));
  const yoyVacCreated = yoyMap(yoy?.vacation_by_created);
  const yoyVacStart = yoyMap(yoy?.vacation_by_collective_start);
  const yoyWfhCreated = yoyMap(yoy?.wfh_by_created);
  const yoyYears = yoy?.years || [];
  const yoyHasData = yoyVacCreated.some(r => yoyYears.some(y => r[String(y)] > 0))
                  || yoyVacStart.some(r => yoyYears.some(y => r[String(y)] > 0))
                  || yoyWfhCreated.some(r => yoyYears.some(y => r[String(y)] > 0));

  // ── Iter56: Fund breakdown (2-fond) ──────────────────────────────────────
  const fundTotals = fund?.totals || null;
  const curYear = fund?.items?.[0]?.current_year || new Date(data.today).getFullYear();
  const prevYear = curYear - 1;
  const fundPie = fundTotals ? (() => {
    const remainingCur = Math.max(0, fundTotals.fond - fundTotals.used_decided - fundTotals.used_pending);
    return [
      { name: `Iskorišteno fond ${curYear}.`, value: fundTotals.used_decided },
      { name: `Iskorišteno prijenos ${prevYear}.`, value: fundTotals.prev_year_iskoristeno || 0 },
      { name: "Rezervirano (pending)", value: fundTotals.used_pending },
      { name: `Preostalo fond ${curYear}.`, value: remainingCur },
      { name: `Preostalo prijenos ${prevYear}.`, value: fundTotals.prev_carryover_available || 0 },
    ].filter(s => s.value > 0);
  })() : [];
  const fundItems = fund?.items || [];
  const fundQLower = fundQ.trim().toLowerCase();
  const fundFiltered = (() => {
    let arr = fundItems;
    if (fundQLower) {
      arr = arr.filter(it =>
        (it.full_name || "").toLowerCase().includes(fundQLower)
        || (it.email || "").toLowerCase().includes(fundQLower)
        || (it.katedra || "").toLowerCase().includes(fundQLower)
      );
    }
    if (onlyPending) {
      arr = arr.filter(it => !it.has_submitted);
    }
    return arr;
  })();
  const filteredEmails = fundFiltered.map(it => it.email);
  const selectableEmails = filteredEmails.filter(e => {
    const it = fundFiltered.find(x => x.email === e);
    return it && !it.has_submitted;
  });
  const selectedInView = filteredEmails.filter(e => selected.has(e));
  const allSelectable = selectableEmails.length > 0 && selectableEmails.every(e => selected.has(e));
  const someSelectable = selectableEmails.length > 0 && selectableEmails.some(e => selected.has(e));
  const pendingTotal = fundItems.filter(it => !it.has_submitted).length;
  const submittedTotal = fundItems.length - pendingTotal;
  const decisionTotal = fundItems.filter(it => it.has_decision).length;
  const submittedNoDecisionTotal = submittedTotal - decisionTotal;
  const allPendingEmails = fundItems.filter(it => !it.has_submitted).map(it => it.email);

  const toggleRow = (email, has_submitted) => {
    if (has_submitted) return;  // već je predao, ne dopuštaj odabir
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email); else next.add(email);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelectable) {
        selectableEmails.forEach(e => next.delete(e));
      } else {
        selectableEmails.forEach(e => next.add(e));
      }
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const sendRemindersTo = async (emails) => {
    if (emails.length === 0) {
      toast.error("Nije odabran nijedan zaposlenik.");
      return;
    }
    setSending(true);
    try {
      const r = await api.post("/admin/stats/send-reminders", { emails });
      const { sent, failed, skipped_already_submitted, escalated } = r.data;
      if (sent > 0) {
        toast.success(`Podsjetnik poslan: ${sent} ${sent === 1 ? "zaposleniku" : "zaposlenika"}` +
          (escalated > 0 ? ` (eskalirano voditeljima: ${escalated})` : ""));
      }
      if (skipped_already_submitted > 0) {
        toast.info(`Preskočeno (već predali zahtjev): ${skipped_already_submitted}`);
      }
      if (failed > 0) {
        const firstErr = r.data.failed_emails?.[0]?.error || "";
        toast.error(`Neuspješno: ${failed}. ${firstErr.includes("verify a domain") ? "Resend testni mod — domena nije verificirana." : firstErr.slice(0, 120)}`);
      }
      // Refresh fund-breakdown da has_submitted bude točan
      api.get("/admin/stats/fund-breakdown").then(rr => setFund(rr.data)).catch(() => {});
      clearSelection();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Greška pri slanju podsjetnika.");
    } finally {
      setSending(false);
    }
  };
  const sendReminders = () => sendRemindersTo(Array.from(selected));

  return (
    <div className="space-y-6" data-testid="statistika-page">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-wider">Pregled za dekanat</p>
        <h1 className="serif text-4xl font-semibold">Statistike</h1>
        <p className="text-muted-foreground mt-1">
          Tjedan: {hr(data.week_start)} – {hr(data.week_end)} · Ukupno zaposlenika: <b>{data.total_employees}</b>
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={UsersIcon} label="Trenutno na GO" value={data.on_vacation_today.length} sub={`od ${data.total_employees} zaposlenika`} />
        <StatCard icon={CalendarDays} label="Ovog tjedna na GO" value={data.on_vacation_this_week.length} sub={`${hr(data.week_start)}–${hr(data.week_end)}`}/>
        <StatCard icon={TrendingUp} label="U postupku suglasnosti" value={totalPendingApprovals} sub={`${o.vacation.pending || 0} GO + ${o.wfh.pending || 0} WFH`}/>
        <StatCard icon={BarChart3} label="Suglasan od voditelja (GO)" value={totalApproved} sub="ukupno u sustavu"/>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">GO dani po mjesecima ({new Date(data.today).getFullYear()}.)</CardTitle></CardHeader>
          <CardContent className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthData} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                <Tooltip formatter={(v) => `${v} radnih dana`} />
                <Bar dataKey="days" fill="#0B2545" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Status zahtjeva (GO)</CardTitle></CardHeader>
          <CardContent className="h-72">
            {statusPie.length === 0
              ? <p className="text-sm text-muted-foreground p-4">Nema zahtjeva.</p>
              : <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={statusPie} dataKey="value" nameKey="name"
                         outerRadius={80} label={(e) => `${e.name}: ${e.value}`}>
                      {statusPie.map((s, i) => <Cell key={s.name} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Tooltip/>
                  </PieChart>
                </ResponsiveContainer>}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top 10 katedri po iskorištenim GO danima</CardTitle>
        </CardHeader>
        <CardContent className="h-96">
          {katedraData.length === 0
            ? <p className="text-sm text-muted-foreground">Nema podataka.</p>
            : <ResponsiveContainer width="100%" height="100%">
                <BarChart data={katedraData} layout="vertical" margin={{ top: 8, right: 16, bottom: 0, left: 100 }}>
                  <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false}/>
                  <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={140} />
                  <Tooltip formatter={(v, name) => [v, name === "GO" ? "GO dani" : "WFH zahtjeva"]} labelFormatter={(label, payload) => payload?.[0]?.payload?.fullName || label}/>
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="GO" fill="#0B2545" />
                  <Bar dataKey="WFH" fill="#C9A961" />
                </BarChart>
              </ResponsiveContainer>}
        </CardContent>
      </Card>

      {/* ── Year-on-Year comparative trends ────────────────────────── */}
      {yoy && yoyHasData && (
        <div className="space-y-4" data-testid="yoy-section">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <LineChartIcon className="w-4 h-4"/>Godina-na-godinu trendovi
            </p>
            <h2 className="serif text-2xl font-semibold mt-0.5">Usporedba {yoyYears.join(" · ")}</h2>
            <p className="text-xs text-muted-foreground mt-1">
              Trend predaje i korištenja za zadnje 3 godine — Q1 navala, ljetni kolektivni, Božićni mir vide se na prvi pogled.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card data-testid="yoy-vac-created">
              <CardHeader>
                <CardTitle className="text-base">GO zahtjevi po mjesecu PREDAJE</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Kad se ljudi prijavljuju za godišnji — pokazuje sezonsku raspodjelu.
                </p>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <BarChart data={yoyVacCreated} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                    <Tooltip formatter={(v) => `${v} zahtjeva`}/>
                    <Legend wrapperStyle={{ fontSize: 11 }}/>
                    {yoyYears.map((y, i) => (
                      <Bar key={y} dataKey={String(y)} fill={COLORS[i % COLORS.length]} radius={[4, 4, 0, 0]}/>
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            <Card data-testid="yoy-vac-start">
              <CardHeader>
                <CardTitle className="text-base">GO odmori po mjesecu POČETKA</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Kad se ljudi odmaraju (samo odobreni / rješenje izdano / poslano).
                </p>
              </CardHeader>
              <CardContent className="h-72">
                <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                  <LineChart data={yoyVacStart} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                    <Tooltip formatter={(v) => `${v} godišnjih`}/>
                    <Legend wrapperStyle={{ fontSize: 11 }}/>
                    {yoyYears.map((y, i) => (
                      <Line key={y} type="monotone" dataKey={String(y)} stroke={COLORS[i % COLORS.length]} strokeWidth={2.5} dot={{ r: 4 }}/>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          <Card data-testid="yoy-wfh-created">
            <CardHeader>
              <CardTitle className="text-base">Rad na daljinu — zahtjevi po mjesecu predaje</CardTitle>
              <p className="text-xs text-muted-foreground">
                Pokazuje hoće li navika rada od kuće rasti, padati ili sezonski oscilirati.
              </p>
            </CardHeader>
            <CardContent className="h-72">
              <ResponsiveContainer width="100%" height="100%" minHeight={200}>
                <BarChart data={yoyWfhCreated} margin={{ top: 8, right: 16, bottom: 0, left: -16 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false}/>
                  <Tooltip formatter={(v) => `${v} zahtjeva`}/>
                  <Legend wrapperStyle={{ fontSize: 11 }}/>
                  {yoyYears.map((y, i) => (
                    <Bar key={y} dataKey={String(y)} fill={COLORS[(i + 2) % COLORS.length]} radius={[4, 4, 0, 0]}/>
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Iter56: Razdioba fonda godišnjeg (2-fond logika) ──────────────── */}
      {fund && fundTotals && (
        <div className="space-y-4" data-testid="fund-breakdown-section">
          <div>
            <p className="text-sm text-muted-foreground uppercase tracking-wider flex items-center gap-2">
              <Wallet className="w-4 h-4"/>Razdioba fonda godišnjeg
            </p>
            <h2 className="serif text-2xl font-semibold mt-0.5">
              Prijenos {prevYear}. + Fond {curYear}.
            </h2>
            <p className="text-xs text-muted-foreground mt-1">
              Agregirani prikaz fonda po zaposleniku (134 osoba). Stari fond se troši prvi — istječe 30.06.{curYear}.
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              icon={Wallet}
              label={`Ukupni fond ${curYear}.`}
              value={fundTotals.fond}
              sub={`${fundItems.length} zaposlenika · ⌀ ${fundItems.length ? (fundTotals.fond / fundItems.length).toFixed(1) : 0} d`}
            />
            <StatCard
              icon={BarChart3}
              label="Iskorišteno (odobreno)"
              value={fundTotals.used_decided}
              sub={`baseline ${fundTotals.baseline_current} + zahtjevi ${fundTotals.auto_current}`}
            />
            <StatCard
              icon={CalendarDays}
              label={`Prijenos ${prevYear}. preostao`}
              value={fundTotals.prev_carryover_available || 0}
              sub={fundTotals.prev_year_iskoristeno > 0 ? `iskorišteno: ${fundTotals.prev_year_iskoristeno}` : "—"}
            />
            <StatCard
              icon={TrendingUp}
              label="Preostalo ukupno"
              value={fundTotals.available}
              sub={`pending: ${fundTotals.used_pending}`}
            />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card data-testid="fund-pie-card">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <PieIcon className="w-4 h-4"/>Razdioba ukupnog fonda
                </CardTitle>
                <p className="text-xs text-muted-foreground">
                  Vizualni omjer: što je već iskorišteno iz starog/novog fonda i koliko ostaje.
                </p>
              </CardHeader>
              <CardContent className="h-80">
                {fundPie.length === 0
                  ? <p className="text-sm text-muted-foreground p-4">Nema podataka.</p>
                  : <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={fundPie} dataKey="value" nameKey="name"
                             innerRadius={55} outerRadius={95} paddingAngle={2}
                             label={(e) => `${e.value}`}>
                          {fundPie.map((s, i) => <Cell key={s.name} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => `${v} dana`} />
                      </PieChart>
                    </ResponsiveContainer>}
              </CardContent>
            </Card>

            <Card data-testid="fund-summary-card">
              <CardHeader>
                <CardTitle className="text-base">Razdioba — brojčani sažetak</CardTitle>
                <p className="text-xs text-muted-foreground">
                  Točan zbroj svih komponenti agregiranog fonda.
                </p>
              </CardHeader>
              <CardContent className="text-sm space-y-2.5">
                <div className="flex justify-between items-baseline border-b pb-2">
                  <span className="text-muted-foreground">Ukupni fond {curYear}.</span>
                  <span className="serif text-xl font-semibold">{fundTotals.fond}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground">— baseline (preglas)</span>
                  <span>{fundTotals.baseline_current}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground">— zahtjevi (auto)</span>
                  <span>{fundTotals.auto_current}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground">— pending</span>
                  <span className="text-amber-700">{fundTotals.used_pending}</span>
                </div>
                <div className="flex justify-between items-baseline border-b pb-2 pt-1">
                  <span className="font-medium">Iskorišteno + rezervirano</span>
                  <span className="font-medium">{fundTotals.used_decided + fundTotals.used_pending}</span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-muted-foreground">Prijenos {prevYear}. (iskorišteno)</span>
                  <span>{fundTotals.prev_year_iskoristeno}</span>
                </div>
                <div className="flex justify-between items-baseline border-b pb-2">
                  <span className="text-muted-foreground">Prijenos {prevYear}. (još aktivno)</span>
                  <span className="text-emerald-700">{fundTotals.prev_carryover_available}</span>
                </div>
                <div className="flex justify-between items-baseline pt-1">
                  <span className="serif text-base font-semibold">Preostalo ukupno</span>
                  <span className="serif text-2xl font-semibold text-primary">{fundTotals.available}</span>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card data-testid="fund-table-card">
            <CardHeader className="space-y-3">
              <div className="flex flex-row items-center justify-between gap-3 flex-wrap">
                <div>
                  <CardTitle className="text-base">Razdioba po zaposleniku</CardTitle>
                  <p className="text-xs text-muted-foreground">
                    Klikom na red → povijest (GO+WFH). Checkbox → odaberi za bulk podsjetnik. Pretraga po imenu/e-mailu/katedri.
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {/* Iter66: one-click podsjetnik svim nepredanima */}
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        type="button"
                        size="sm"
                        disabled={pendingTotal === 0 || sending}
                        data-testid="fund-remind-all-pending"
                      >
                        <Send className="w-3.5 h-3.5 mr-1.5"/>
                        {sending ? "Slanje…" : `Podsjeti sve nepredane (${pendingTotal})`}
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Poslati podsjetnik svima koji nisu predali zahtjev?</AlertDialogTitle>
                        <AlertDialogDescription>
                          E-mail podsjetnik za predaju zahtjeva za godišnji odmor bit će poslan na{" "}
                          <b>{pendingTotal}</b> zaposlenika koji još nisu predali zahtjev za {curYear}. godinu.
                          Zaposlenici koji su već predali automatski se preskaču.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid="remind-all-cancel">Odustani</AlertDialogCancel>
                        <AlertDialogAction data-testid="remind-all-confirm"
                                           onClick={() => sendRemindersTo(allPendingEmails)}>
                          Pošalji ({pendingTotal})
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  <Button
                    type="button"
                    variant={onlyPending ? "default" : "outline"}
                    size="sm"
                    data-testid="fund-toggle-pending"
                    onClick={() => setOnlyPending(v => !v)}
                  >
                    <MailWarning className="w-3.5 h-3.5 mr-1.5"/>
                    {onlyPending ? `Samo nepredani (${pendingTotal})` : `Filter: nepredani (${pendingTotal})`}
                  </Button>
                  <div className="relative w-full sm:w-72">
                    <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                    <Input
                      data-testid="fund-search"
                      className="pl-8"
                      placeholder="Pretraga: ime, e-mail, katedra…"
                      value={fundQ}
                      onChange={(e) => setFundQ(e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {/* Iter66: pregled tko je dobio rješenje / predao / nije predao */}
              <div className="flex items-center gap-2 flex-wrap" data-testid="fund-status-summary">
                <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]" data-testid="summary-decision">
                  <CheckCircle2 className="w-3 h-3 mr-1"/>Rješenje izdano: {decisionTotal}
                </Badge>
                <Badge variant="secondary" className="text-[10px]" data-testid="summary-submitted">
                  Predali (čeka rješenje): {submittedNoDecisionTotal}
                </Badge>
                <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300" data-testid="summary-pending">
                  <AlertCircle className="w-3 h-3 mr-1"/>Nisu predali: {pendingTotal}
                </Badge>
              </div>
              {selected.size > 0 && (
                <div
                  data-testid="fund-bulk-bar"
                  className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-md border bg-primary/5 border-primary/20"
                >
                  <div className="flex items-center gap-2 text-sm">
                    <CheckCircle2 className="w-4 h-4 text-primary"/>
                    <span className="font-medium">Odabrano: {selected.size}</span>
                    {selectedInView.length !== selected.size && (
                      <span className="text-xs text-muted-foreground">
                        ({selectedInView.length} u trenutnom filteru)
                      </span>
                    )}
                    <button
                      type="button"
                      className="text-xs underline text-muted-foreground hover:text-primary ml-2"
                      onClick={clearSelection}
                      data-testid="fund-bulk-clear"
                    >
                      Poništi odabir
                    </button>
                  </div>
                  <Button
                    type="button"
                    onClick={sendReminders}
                    disabled={sending}
                    data-testid="fund-send-reminders"
                    size="sm"
                  >
                    <Send className="w-3.5 h-3.5 mr-1.5"/>
                    {sending ? "Slanje…" : `Pošalji podsjetnik (${selected.size})`}
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[480px] overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-secondary/40 sticky top-0 z-10">
                    <tr className="text-left">
                      <th className="p-2.5 w-8">
                        <Checkbox
                          data-testid="fund-select-all"
                          checked={allSelectable ? true : (someSelectable ? "indeterminate" : false)}
                          onCheckedChange={toggleSelectAll}
                          disabled={selectableEmails.length === 0}
                          aria-label="Odaberi sve nepredane u filteru"
                        />
                      </th>
                      <th className="p-2.5 font-medium">Zaposlenik</th>
                      <th className="p-2.5 font-medium">Katedra</th>
                      <th className="p-2.5 font-medium">Status</th>
                      <th className="p-2.5 font-medium text-right">Fond {curYear}.</th>
                      <th className="p-2.5 font-medium text-right">Iskorišteno</th>
                      <th className="p-2.5 font-medium text-right">Pending</th>
                      <th className="p-2.5 font-medium text-right">Prijenos {prevYear}.</th>
                      <th className="p-2.5 font-medium text-right">Preostalo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fundFiltered.length === 0 ? (
                      <tr><td colSpan={9} className="p-6 text-center text-muted-foreground">Nema rezultata.</td></tr>
                    ) : fundFiltered.map((it) => {
                      const usedTip = `baseline ${it.baseline_current} + zahtjevi ${it.auto_current}`;
                      const isChecked = selected.has(it.email);
                      return (
                        <tr key={it.email}
                            className={`border-t hover:bg-secondary/40 transition-colors ${isChecked ? "bg-primary/5" : ""}`}
                            data-testid={`fund-row-${it.email}`}>
                          <td className="p-2.5 align-middle" onClick={(e) => e.stopPropagation()}>
                            <Checkbox
                              checked={isChecked}
                              onCheckedChange={() => toggleRow(it.email, it.has_submitted)}
                              disabled={it.has_submitted}
                              aria-label={`Odaberi ${it.full_name}`}
                              data-testid={`fund-check-${it.email}`}
                            />
                          </td>
                          <td className="p-2.5 cursor-pointer"
                              onClick={() => setHistoryEmployee(it)}
                              title="Klikni za pregled povijesti zaposlenika">
                            <div className="font-medium">{it.full_name}</div>
                            <div className="text-[10px] text-muted-foreground">{it.email}</div>
                          </td>
                          <td className="p-2.5 text-muted-foreground cursor-pointer" onClick={() => setHistoryEmployee(it)}>
                            {it.katedra || "—"}
                          </td>
                          <td className="p-2.5">
                            {it.has_decision
                              ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1"/>Rješenje</Badge>
                              : it.has_submitted
                                ? <Badge variant="secondary" className="text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1"/>Predao</Badge>
                                : <Badge variant="outline" className="text-[10px] text-amber-700 border-amber-300"><AlertCircle className="w-3 h-3 mr-1"/>Nepredan</Badge>}
                          </td>
                          <td className="p-2.5 text-right tabular-nums">{it.fond}</td>
                          <td className="p-2.5 text-right tabular-nums" title={usedTip}>
                            {it.used_decided}
                            {(it.baseline_current > 0) && (
                              <span className="text-[10px] text-muted-foreground ml-1">({it.baseline_current}+{it.auto_current})</span>
                            )}
                          </td>
                          <td className="p-2.5 text-right tabular-nums text-amber-700">{it.used_pending || ""}</td>
                          <td className="p-2.5 text-right tabular-nums">
                            {it.prev_carryover_available > 0
                              ? <span className="text-emerald-700">{it.prev_carryover_available}</span>
                              : <span className="text-muted-foreground">—</span>}
                          </td>
                          <td className="p-2.5 text-right tabular-nums font-semibold">{it.available}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {fundFiltered.length > 0 && (
                    <tfoot className="bg-secondary/30 sticky bottom-0">
                      <tr className="border-t font-medium">
                        <td className="p-2.5" colSpan={4}>Zbroj ({fundFiltered.length}) · {submittedTotal} predalo / {pendingTotal} nepredano (svi)</td>
                        <td className="p-2.5 text-right tabular-nums">{fundFiltered.reduce((a,b)=>a+(b.fond||0),0)}</td>
                        <td className="p-2.5 text-right tabular-nums">{fundFiltered.reduce((a,b)=>a+(b.used_decided||0),0)}</td>
                        <td className="p-2.5 text-right tabular-nums text-amber-700">{fundFiltered.reduce((a,b)=>a+(b.used_pending||0),0)}</td>
                        <td className="p-2.5 text-right tabular-nums">{fundFiltered.reduce((a,b)=>a+(b.prev_carryover_available||0),0)}</td>
                        <td className="p-2.5 text-right tabular-nums">{fundFiltered.reduce((a,b)=>a+(b.available||0),0)}</td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle className="text-base">Trenutno na GO — {hr(data.today)}</CardTitle></CardHeader>
          <CardContent className="p-3">
            {data.on_vacation_today.length === 0
              ? <p className="text-xs text-muted-foreground p-3">Nitko nije na godišnjem danas.</p>
              : <ul className="space-y-1.5">
                  {data.on_vacation_today.map((e, i) => (
                    <li key={`${e.employee_email}-${e.from}`} className="flex items-center justify-between text-sm border rounded p-2" data-testid={`today-${i}`}>
                      <div>
                        <div className="font-medium">{e.full_name}</div>
                        <div className="text-[11px] text-muted-foreground">{e.katedra}</div>
                      </div>
                      <Badge variant="outline" className="text-[11px]">do {hr(e.to)}</Badge>
                    </li>
                  ))}
                </ul>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Ovaj tjedan na GO ({data.on_vacation_this_week.length})</CardTitle></CardHeader>
          <CardContent className="p-3">
            {data.on_vacation_this_week.length === 0
              ? <p className="text-xs text-muted-foreground p-3">Nitko nije na godišnjem ovaj tjedan.</p>
              : <ul className="space-y-1.5 max-h-72 overflow-y-auto">
                  {data.on_vacation_this_week.map((e, i) => (
                    <li key={`${e.employee_email}-${e.from}`} className="flex items-center justify-between text-sm border rounded p-2" data-testid={`week-${i}`}>
                      <div>
                        <div className="font-medium">{e.full_name}</div>
                        <div className="text-[11px] text-muted-foreground">{e.katedra}</div>
                      </div>
                      <Badge variant="outline" className="text-[11px]">{hr(e.from)} – {hr(e.to)}</Badge>
                    </li>
                  ))}
                </ul>}
          </CardContent>
        </Card>
      </div>

      <PayrollReportCard />

      <EmployeeHistorySheet
        employee={historyEmployee}
        onClose={() => setHistoryEmployee(null)}
      />
    </div>
  );
}
